import { useEffect, useMemo, useState } from "react";
import type { InstanceActivityEvent, InstanceDetail, InstanceSnapshot } from "@keydials/api-client";
import { Dial } from "@keydials/ui";
import { useWorkspaceServices } from "../context";
import { instanceToDialModel } from "../instanceModel";
import { DEFAULT_SEGMENT_NOUN, type SegmentNoun } from "../vocabulary";
import { fmtDate, timeAgo } from "../lib/ui";
import { activityActor } from "./ActivityPanel";
import { NOTHING_TO_LOOK_BACK_ON, noActivityYet } from "./compare/compareModel";

const PRESETS: { id: string; label: string; days: number }[] = [
  { id: "week", label: "A week ago", days: 7 },
  { id: "month", label: "A month ago", days: 30 },
  { id: "quarter", label: "Three months ago", days: 91 },
  { id: "year", label: "This time last year", days: 365 },
];

function daysAgo(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d;
}
function toInputDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * "Look back" (KEY-LOOKBACK): the dial as it was. Pick a day and the face redraws with the colours it had
 * then — replayed on the server from the status ledgers and rolled up under today's rules, so a past face
 * and the live one are coloured by one rule — with what changed between then and now beneath it. The picture
 * is the report; the list is the caption.
 */
export function LookBackPanel({ detail, noun = DEFAULT_SEGMENT_NOUN }: {
  detail: InstanceDetail;
  noun?: SegmentNoun;
}) {
  const { api, getAccessToken } = useWorkspaceServices();
  const [day, setDay] = useState<string>(toInputDate(daysAgo(30)));
  const [snapshot, setSnapshot] = useState<InstanceSnapshot | null>(null);
  // The dial's whole feed, once per dial: it decides "nothing to look back on yet" and, filtered to after the
  // chosen day, is the caption under the face.
  const [allEvents, setAllEvents] = useState<InstanceActivityEvent[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // The end of the chosen day, in the browser's own zone: "as it was on the 6th" means by the end of the 6th.
  const at = useMemo(() => new Date(`${day}T23:59:59`), [day]);
  // KEY-760: a dial nothing has happened on yet — a brand-new one — has no past. Say so, and offer no day to pick.
  const empty = noActivityYet(allEvents);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;
    let cancelled = false;
    setAllEvents(null);
    api.getInstanceActivity(token, detail.id)
      .then((acts) => { if (!cancelled) setAllEvents(acts); })
      // The feed is the caption, not the picture: without it the face still shows, just with nothing under it.
      .catch(() => { /* unknown is not empty */ });
    return () => { cancelled = true; };
  }, [api, getAccessToken, detail.id, attempt]);

  useEffect(() => {
    const token = getAccessToken();
    if (!token || Number.isNaN(at.getTime()) || empty) return;
    let cancelled = false;
    setFailed(false);
    setSnapshot(null);
    api.instanceAsOf(token, detail.id, at.toISOString())
      .then((snap) => { if (!cancelled) setSnapshot(snap); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [api, getAccessToken, detail.id, at, empty, attempt]);

  const events = useMemo(
    () => allEvents?.filter((e) => new Date(e.occurredAtUtc).getTime() > at.getTime()) ?? null,
    [allEvents, at],
  );

  // The past face: today's detail with the replayed statuses and rollup swapped in.
  const model = useMemo(() => {
    if (!snapshot) return null;
    const then: InstanceDetail = {
      ...detail,
      statuses: snapshot.statuses.map((s) => ({ ...s, reviewDate: null, updatedAtUtc: snapshot.atUtc, updatedByEmail: "" })),
      itemStatuses: snapshot.itemStatuses.map((s) => ({ ...s, updatedAtUtc: snapshot.atUtc, updatedByEmail: "" })),
      rollup: snapshot.rollup,
    };
    return instanceToDialModel(then);
  }, [snapshot, detail]);

  const statusChanges = events?.filter((e) => e.kind === "ItemStatus" || e.kind === "SegmentStatus" || e.kind === "SectionStatus").length ?? 0;

  return (
    <div className="kd-card" style={{ padding: "var(--pad-card)", display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, color: "var(--text-muted)" }}>The dial as it was on</span>
        <input
          type="date"
          className="kd-focusable"
          aria-label="Look back to"
          value={day}
          max={toInputDate(new Date())}
          disabled={empty}
          onChange={(e) => e.target.value && setDay(e.target.value)}
          style={{ fontSize: 13, padding: "4px 8px", borderRadius: 8, border: "1px solid var(--border)", background: "transparent", color: "var(--text)" }}
        />
        <div role="group" aria-label="Jump to" style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
          {PRESETS.map((p) => {
            const value = toInputDate(daysAgo(p.days));
            return (
              <button
                key={p.id}
                type="button"
                className="kd-focusable"
                aria-pressed={!empty && day === value}
                disabled={empty}
                onClick={() => setDay(value)}
                style={{
                  fontSize: 12, padding: "4px 10px", borderRadius: 999, cursor: empty ? "default" : "pointer",
                  opacity: empty ? 0.5 : 1,
                  border: "1px solid var(--border)",
                  background: day === value ? "var(--text)" : "transparent",
                  color: day === value ? "var(--bg, #fff)" : "var(--text-muted)",
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {empty ? (
        <p role="status" style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>{NOTHING_TO_LOOK_BACK_ON}</p>
      ) : failed ? (
        <p role="alert" style={{ margin: 0, fontSize: 13, color: "var(--text-faint)" }}>
          Couldn’t look back just now.{" "}
          <button
            type="button"
            className="kd-focusable"
            onClick={() => { setFailed(false); setAttempt((n) => n + 1); }}
            style={{ font: "inherit", fontWeight: 700, background: "none", border: "none", padding: 0, color: "var(--accent-text)", textDecoration: "underline", cursor: "pointer" }}
          >
            Try again
          </button>
        </p>
      ) : !model ? (
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-faint)" }}>Turning the clock back…</p>
      ) : (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Dial
            model={model}
            size={360}
            ariaLabel={`${detail.name} as it was on ${fmtDate(day)}`}
            sectionWord="Section"
            segmentWord={noun.One}
          />
        </div>
      )}

      {events && !empty && (
        <section aria-label="What changed since">
          <h4 style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--text-muted)" }}>
            Since then · {events.length === 0 ? "nothing changed" : `${events.length} change${events.length === 1 ? "" : "s"}${statusChanges > 0 ? `, ${statusChanges} to a status` : ""}`}
          </h4>
          {events.length > 0 && (
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column" }}>
              {events.slice(0, 25).map((e, i) => (
                <li key={`${e.occurredAtUtc}-${i}`} style={{ display: "flex", gap: 10, padding: "7px 0", borderTop: "var(--border-w) solid var(--border)", fontSize: 13 }}>
                  <span style={{ flex: "1 1 auto", minWidth: 0, color: "var(--text)" }}>
                    {e.description}
                    <span style={{ marginLeft: 6, fontSize: 12, color: "var(--text-faint)" }}>· {activityActor(e)}</span>
                  </span>
                  <span style={{ flex: "none", fontSize: 12, color: "var(--text-faint)" }}>{timeAgo(e.occurredAtUtc)}</span>
                </li>
              ))}
              {events.length > 25 && <li style={{ padding: "7px 0", fontSize: 12, color: "var(--text-faint)" }}>…and {events.length - 25} more in Activity.</li>}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
